import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Task } from "types/Models/task";

type IntakeEntry = NonNullable<Task["state_registration_intake"]>[number];

const FIELD_LABEL_MAP: Record<string, string> = {
  // SOS fields (from constants.ts field names)
  date_of_incorporation: "Date of Incorporation",
  state_of_incorporation: "State of Incorporation",
  entity_type: "Entity Type",
  principal_business_activity: "Principal Business Activity",
  business_start_date_SOS: "Business start date",
  business_address_in_state: "Business address in the state",
  authorized_shares: "Number of authorized shares",
  registered_agent_name_address: "Registered Agent Name & Address",
  ra_agent_name: "Agent Name",
  ra_start_date: "Starting Date",
  ra_expiration_date: "Expiration Date",
  ra_address: "Address",
  ra_city: "City",
  ra_zipcode: "Zip / Postal Code",
  ra_phone_number: "Phone No.",
  // Payroll fields
  num_employees: "Number of Employees",
  first_payroll_date: "First Payroll Date",
  employee_names_addresses: "Employee Names & Addresses",
  federal_ein: "Federal EIN",
  // Sales tax fields
  business_start_date: "Business Start Date",
  first_sale_date: "First Sale Date",
  product_service_type: "Product / Service Type",
  annual_revenue: "Annual Revenue",
  business_address: "Business Address",
  // Legacy keys (in case older tasks used different names)
  incorporation_date: "Date of Incorporation",
  incorporation_state: "State of Incorporation",
  business_activity: "Principal Business Activity",
  agent_name: "Registered Agent Name",
  agent_address: "Registered Agent Address",
  product_type: "Product / Service Type",
  address: "Business Address",
};

const REGISTRATION_TYPE_LABEL: Record<string, string> = {
  SECRETARY_OF_STATE: "Secretary of State",
  REGISTERED_AGENT: "Registered Agent",
  PAY_ROLL: "Payroll",
  SALES_TAX: "Sales Tax",
};

function IntakeEntrySection({ entry }: { entry: IntakeEntry }) {
  const fields = Object.entries(entry.intake_data).filter(([, v]) => v);
  if (fields.length === 0) return null;

  const typeLabel =
    REGISTRATION_TYPE_LABEL[entry.registration_type] || entry.registration_type;

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {fields.length > 0 && (
        <p className="t-text-body-sm t-font-medium t-text-text-60">
          {typeLabel}
        </p>
      )}
      <div className="t-grid t-grid-cols-2 t-gap-x-8 t-gap-y-3">
        {fields.map(([key, value]) => (
          <div key={key} className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-body-sm t-text-text-30">
              {FIELD_LABEL_MAP[key] || key.replace(/_/g, " ")}
            </span>
            <span className="t-text-body-sm t-text-text-100">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StateRegistrationIntakePanel({
  intakeData,
}: {
  intakeData: NonNullable<Task["state_registration_intake"]>;
}) {
  const nonEmpty = intakeData.filter((entry) =>
    Object.values(entry.intake_data).some((v) => v)
  );

  if (nonEmpty.length === 0) return null;

  return (
    <div className="t-mx-5 t-mt-4">
      <Accordion.Root type="single" collapsible defaultValue="intake">
        <Accordion.Item value="intake">
          <Accordion.Trigger>Filing Details</Accordion.Trigger>
          <Accordion.Content>
            <div className="t-flex t-flex-col t-gap-6">
              {nonEmpty.map((entry, i) => (
                <IntakeEntrySection key={i} entry={entry} />
              ))}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
}
